import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAddTeamSessionCredits } from "@/lib/teamRecordQueries";

/**
 * Clients Restructure — Team 360 -> Sessions -> "+ Add sessions".
 * Adds entitlement/credits ONLY to the shared TEAM pool
 * (owner_member_id=null) — never touches or creates a Session, and
 * never touches any individual member's own credit balance.
 */
export default function AddTeamSessionsModal({ workspaceId, onClose, onSuccess, nested }) {
  const [quantity, setQuantity] = useState(1);
  const addCredits = useAddTeamSessionCredits(workspaceId);

  async function handleSubmit() {
    try {
      await addCredits.mutateAsync({ quantity: Number(quantity) });
      toast.success(`Added ${quantity} team session${Number(quantity) === 1 ? "" : "s"}`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't add team sessions");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={nested ? "max-w-md !z-[100]" : "max-w-md"}
        overlayClassName={nested ? "!z-[90]" : undefined}
        data-testid="add-team-sessions-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-serif">Add team sessions</DialogTitle>
          <DialogDescription>
            Tops up the Team's shared session pool — separate from any individual member's own credits.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label className="text-xs text-ink-soft uppercase mb-1.5 block">Number of sessions</label>
          <Input
            type="number" min={1} value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
            data-testid="add-team-sessions-quantity-input"
          />
        </div>

        <DialogFooter>
          <button onClick={onClose} className="text-sm text-ink/60 px-4 py-2" data-testid="add-team-sessions-cancel-btn">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={addCredits.isPending || quantity < 1}
            className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
            data-testid="add-team-sessions-submit-btn"
          >
            {addCredits.isPending ? "Adding…" : `Add ${quantity || 0} session${Number(quantity) === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
